import React, { useState } from "react";
import { Row, Col } from "antd";
import { useTranslation } from "react-i18next";
import dayjs, { Dayjs } from "dayjs";
import { format, parse } from "date-fns";
import { fr } from "date-fns/locale";
import CalendarModal from "../CalendarModal/CalendarModal";
import TimePickerComponent from "../DateTimePIckerComponent/TimePickerComponent";
import "./LiveDateTimePicker.less";

type SelectedDate = {
  day?: number;
  month: number;
  year: number;
};

type LiveDateTimePickerProps = {
  date: string | null;
  disabledDate: (date: Dayjs) => boolean;
  disabledTime: (current: Dayjs | null) => any;
  onChange: (type: string, data: any) => void;
};

const LiveDateTimePicker: React.FC<LiveDateTimePickerProps> = ({ date, disabledDate, disabledTime, onChange }) => {
  const { t } = useTranslation();
  const [value, setValue] = useState<string>("");
  const [showCalendarModal, setShowCalendarModal] = useState(false);

  const formatDisplayDate = (dateString: string) => {
    if (!dateString) return "";
    try {
      const parsedDate = parse(dateString, "yyyy-MM-dd", new Date());
      if (isNaN(parsedDate.getTime())) {
        console.error("Invalid date format:", dateString);
        return "";
      }
      return format(parsedDate, "dd/MM/yyyy", { locale: fr });
    } catch (error) {
      console.error("Error formatting date:", error);
      return "";
    }
  };

  const handleDateSelect = (selectedDate: SelectedDate) => {
    if (selectedDate.day === undefined) {
      console.error("Day is required but was undefined");
      return;
    }

    const day = selectedDate.day.toString().padStart(2, "0");
    const month = (selectedDate.month + 1).toString().padStart(2, "0");
    const year = selectedDate.year.toString();

    if (parseInt(day) < 1 || parseInt(day) > 31 || parseInt(month) < 1 || parseInt(month) > 12 || year.length !== 4) {
      console.error("Invalid date components:", { day, month, year });
      return;
    }

    const formattedDate = `${year}-${month}-${day}`;
    setValue(formattedDate);
    onChange("date", dayjs(formattedDate));
    setShowCalendarModal(false);
  };

  return (
    <>
      <Row gutter={7}>
        <Col span={12}>
          <input
            type="text"
            value={value ? formatDisplayDate(value) : ""}
            placeholder={t("select-date") as string}
            className="dateTimePicker"
            onClick={() => setShowCalendarModal(true)}
            readOnly
          />
        </Col>
        <Col span={12}>
          <TimePickerComponent date={value ? dayjs(value).toString() : null} onChange={onChange} />
        </Col>
      </Row>
      {showCalendarModal && (
        <CalendarModal
          title={t("select-a-date") as string}
          onClose={() => setShowCalendarModal(false)}
          onDateSelect={handleDateSelect}
          disablePastDates={true}
        />
      )}
    </>
  );
};

export default LiveDateTimePicker;
